
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { getCategories } from "@/services/promptService";
import type { Category } from "@/services/promptService";

interface UseExploreInitializationProps {
  categoryParam: string | null;
}

export function useExploreInitialization({ categoryParam }: UseExploreInitializationProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [categoryIdFromSlug, setCategoryIdFromSlug] = useState<string | null>(null);

  // State flags for preventing race conditions
  const initialCategoriesLoaded = useRef(false);
  const initialLoadComplete = useRef(false);
  const isMounted = useRef(true);
  const categoriesLoadAttempted = useRef(false);

  console.log('🏗️ useExploreInitialization: Starting with categoryParam:', categoryParam);

  // Set up mounted ref for cleanup
  useEffect(() => {
    isMounted.current = true;
    console.log("🔧 useExploreInitialization: Component mounting");
    
    return () => {
      isMounted.current = false;
      console.log("🔧 useExploreInitialization: Component unmounting");
    };
  }, []);

  // Load categories first
  useEffect(() => {
    const loadCategories = async () => {
      // Prevent multiple loading attempts
      if (initialCategoriesLoaded.current || categoriesLoadAttempted.current) {
        console.log("⏭️ useExploreInitialization: Categories already loaded or attempted, skipping");
        return;
      }
      
      categoriesLoadAttempted.current = true;
      console.log("📚 useExploreInitialization: Loading categories...");
      
      try {
        setIsLoading(true);
        const cats = await getCategories();
        
        if (!isMounted.current) {
          console.log("⚠️ useExploreInitialization: Component unmounted during category load");
          return;
        }
        
        // Always set categories even if empty
        setCategories(cats || []);
        console.log(`✅ useExploreInitialization: Categories loaded: ${cats?.length || 0}`);
        
        // Log all categories for debugging
        if (cats && cats.length > 0) {
          console.log('📋 useExploreInitialization: Available categories:');
          cats.forEach(cat => {
            console.log(`  - ${cat.name} (slug: ${cat.slug}, id: ${cat.id})`);
          });
          
          // IMMEDIATE category processing - don't wait for another effect
          if (categoryParam) {
            console.log(`🔍 useExploreInitialization: IMMEDIATELY processing category param: "${categoryParam}"`);
            const matchingCategory = cats.find(cat => 
              cat.slug && cat.slug.toLowerCase() === categoryParam.toLowerCase()
            );
            
            if (matchingCategory) {
              console.log(`✅ useExploreInitialization: IMMEDIATELY found matching category:`, {
                name: matchingCategory.name,
                id: matchingCategory.id,
                slug: matchingCategory.slug
              });
              setCategoryIdFromSlug(matchingCategory.id);
            } else {
              console.warn(`❌ useExploreInitialization: No category found with slug: "${categoryParam}"`);
              setCategoryIdFromSlug(null);
            }
          } else {
            setCategoryIdFromSlug(null);
          }
        }
        
        initialCategoriesLoaded.current = true;
        initialLoadComplete.current = true;
        
        console.log("🎉 useExploreInitialization: Initialization complete");
      } catch (error) {
        console.error("❌ useExploreInitialization: Error loading categories:", error);
        toast.error("Failed to load categories");
        // Even on error, mark as attempted to prevent infinite retries
        initialCategoriesLoaded.current = true;
        initialLoadComplete.current = true;
      } finally {
        if (isMounted.current) {
          setIsLoading(false);
        }
      }
    };
    
    loadCategories();
  }, [categoryParam]);

  // Debug final state
  useEffect(() => {
    console.log('📊 useExploreInitialization: Final state:', {
      categoriesCount: categories.length,
      isLoading,
      categoryIdFromSlug,
      categoryParam,
      initialCategoriesLoaded: initialCategoriesLoaded.current,
      initialLoadComplete: initialLoadComplete.current
    });
  }, [categories.length, isLoading, categoryIdFromSlug, categoryParam]);

  return {
    categories,
    isLoading,
    categoryIdFromSlug,
    initialCategoriesLoaded,
    initialLoadComplete
  };
}
